import { sortBy } from "lodash-es";

import {
  createAppSelector,
  outcomeGroupSelectors,
  outcomeSelectors,
  outcomeSubgroupSelectors,
  pageOutcomeSelectors,
  sectionOutcomeSelectors,
} from "./base";
import { selectCurrentOrg } from "./org";
import { selectSectionIdsForCurrentUser } from "./section";
import {
  NestedOutcomeSubgroupType,
  NestedPageOutcomeType,
  NestedSectionOutcomeType,
} from "./types";

import { OrgType, OutcomeItemType, RubricType } from "components/server-types";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { compactMap, filterMap } from "utils/collections";

export const selectNestedOutcomeGroups = createAppSelector(
  [outcomeGroupSelectors.selectAll, outcomeSubgroupSelectors.selectAll, outcomeSelectors.selectAll],
  (outcomeGroups, outcomeSubgroups, outcomes) => {
    return outcomeGroups.map((outcomeGroup) => {
      let outcomeCount = 0;

      const filteredOutcomes = outcomes.filter(
        (outcome) => outcome.outcome_group_id === outcomeGroup.id,
      );
      outcomeCount += filteredOutcomes.length;

      const filteredOutcomeSubgroups: NestedOutcomeSubgroupType[] = filterMap(
        outcomeSubgroups,
        (outcomeSubgroup) => outcomeSubgroup.outcome_group_id === outcomeGroup.id,
        (outcomeSubgroup) => {
          const filteredSubgroupOutcomes = outcomes.filter(
            (outcome) => outcome.outcome_subgroup_id === outcomeSubgroup.id,
          );

          outcomeCount += filteredSubgroupOutcomes.length;

          return {
            ...outcomeSubgroup,
            outcomes: filteredSubgroupOutcomes,
          };
        },
      );

      return {
        ...outcomeGroup,
        outcomeSubgroups: filteredOutcomeSubgroups,
        outcomes: filteredOutcomes,
        outcomeCount,
      };
    });
  },
);

export const selectFullSectionOutcomes = createAppSelector(
  [sectionOutcomeSelectors.filterForCurrentSection, outcomeSelectors.selectEntities],
  (section_outcomes, outcomes) => {
    const nestedSectionOutcomes = section_outcomes.reduce((acc, section_outcome) => {
      const outcome = outcomes[section_outcome.outcome_id];
      if (outcome) {
        acc.push({
          ...section_outcome,
          outcome,
        });
      }
      return acc;
    }, [] as NestedSectionOutcomeType[]);
    return sortBy(nestedSectionOutcomes, (co) => co.outcome.title.toLowerCase());
  },
);

export const selectNestedPageOutcomes = createAppSelector(
  [
    pageOutcomeSelectors.filterByPageId,
    (state) => state.section_outcomes,
    (state) => state.outcomes,
  ],
  (page_outcomes, section_outcomes, outcomes) => {
    const nestedPageOutcomes = page_outcomes.reduce((acc, page_outcome) => {
      const sectionOutcome = section_outcomes[page_outcome.section_outcome_id];
      const outcome = outcomes[sectionOutcome?.outcome_id];
      if (sectionOutcome && outcome) {
        acc.push({
          ...page_outcome,
          outcome,
          section_outcome: sectionOutcome,
        });
      }
      return acc;
    }, [] as NestedPageOutcomeType[]);
    return sortBy(nestedPageOutcomes, (po) => po.outcome.title.toLowerCase());
  },
);

export const selectOutcomeByPageOutcomeId = createAppSelector(
  [
    pageOutcomeSelectors.selectById,
    sectionOutcomeSelectors.selectEntities,
    outcomeSelectors.selectEntities,
  ],
  (pageOutcome, sectionOutcomes, outcomes) => {
    if (!pageOutcome) return null;
    const sectionOutcome = sectionOutcomes[pageOutcome.section_outcome_id];
    return outcomes[sectionOutcome?.outcome_id] ?? null;
  },
);

export const selectOrgOutcomeRubric = createAppSelector(
  [
    (state, outcomeId: string | undefined) => outcomeId,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (outcomeId, outcomes, org) => {
    if (!outcomeId || !org) return [] as RubricType;
    const outcome = outcomes[outcomeId];
    if (!outcome) return [] as RubricType;
    return determineRubric(org, [outcome]);
  },
);

export const selectSectionOutcomeRubric = createAppSelector(
  [
    (state, sectionOutcomeId?: string | null) => sectionOutcomeId,
    (state) => state.section_outcomes,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (sectionOutcomeId, section_outcomes, outcomes, org) => {
    if (!sectionOutcomeId) return null;
    const section_outcome = section_outcomes[sectionOutcomeId];
    const outcome = outcomes[section_outcome?.outcome_id];
    if (!org || !section_outcome || !outcome) return null;
    return determineRubric(org, [section_outcome, outcome]);
  },
);

export const selectPageOutcomeRubric = createAppSelector(
  [
    pageOutcomeSelectors.selectById,
    sectionOutcomeSelectors.selectEntities,
    outcomeSelectors.selectEntities,
    selectCurrentOrg,
  ],
  (page_outcome, section_outcomes, outcomes, org) => {
    if (!page_outcome) return [] as RubricType;
    const section_outcome = section_outcomes[page_outcome.section_outcome_id];
    const outcome = outcomes[section_outcome?.outcome_id];
    if (!org || !section_outcome || !outcome) return [] as RubricType;
    return determineRubric(org, [page_outcome, section_outcome, outcome]);
  },
);

const determineRubric = (org: OrgType, outcomeItems: OutcomeItemType[]): RubricType => {
  // Create a new rubric object by copying the default rubric for the org, then
  // potentially substituting rubric descriptions from the first supplied OutcomeItem
  // argument that has customized rubric descriptions, if any.
  const outcomeItem = outcomeItems.find((item) => !!item.rubric_descriptions);
  const customDescriptions = outcomeItem && outcomeItem.rubric_descriptions;
  return org.rubric.map((rubricLevel, index) => ({
    ...rubricLevel,
    description: customDescriptions ? customDescriptions[index] : rubricLevel.description,
  }));
};

export const selectOutcomesForCurrentUser = createAppSelector(
  [selectSectionIdsForCurrentUser, sectionOutcomeSelectors.selectAll, (state) => state.outcomes],
  (sectionIds, sectionOutcomes, outcomeMap) => {
    const sectionIdsForUser = new Set(sectionIds);
    const sectionOutcomesForUser = sectionOutcomes.filter((sectionOutcome) =>
      sectionIdsForUser.has(sectionOutcome.section_id),
    );
    // We need to get the outcomes from the section outcomes, but we could get duplicates.
    // We can use a Set to ensure that we only include each outcome ID once.
    const outcomeIdsForUser = new Set(
      sectionOutcomesForUser.map((sectionOutcome) => sectionOutcome.outcome_id),
    );
    return compactMap(Array.from(outcomeIdsForUser), (outcomeId) => outcomeMap[outcomeId]).sort(
      ORDER_BY_TITLE,
    );
  },
);
